import { useNavigate } from "@tanstack/react-router";
import type { GmvBreakdownLens } from "@unbox/api";
import { CursorBox, LayersTwo01, Plus, Users01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { useHomeGmvBreakdown } from "@/api/home";
import { Button } from "@/components/base/buttons/button";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { ProductDetails } from "@/pages/products/product-details";
import type { AskRequest } from "../lib/ask-prompts";
import { BREAKDOWN_COLUMN_HEIGHT } from "../lib/breakdown-scale";
import { headline, LENS_TABS, REMAINDER_NAME, subline } from "../lib/gmv-breakdown-copy";
import type { DistributionRange } from "../lib/ranges";
import { noteRangePhrase } from "../lib/ranges";
import { type BreakdownColumn, GmvBreakdownChart } from "./gmv-breakdown-chart";
import { GmvBreakdownPanel, type PanelEntity } from "./gmv-breakdown-panel";

const REMAINDER_ID = "lens-rest";

// The cohorts page holds no per-cohort view, so that lens lands on the library with
// nothing selected. The creator explorer opens on Best matches unless the tab is named,
// and the breakdown ranks every creator rather than the recommended ones.
const LENS_DESTINATION: Record<GmvBreakdownLens, { path: string; search: Record<string, string> }> = {
  product: { path: "/products/all", search: {} },
  creator: { path: "/creators", search: { classification: "__all" } },
  cohort: { path: "/creators/cohorts", search: {} },
};

const LENS_ICONS: Record<GmvBreakdownLens, typeof CursorBox> = {
  product: CursorBox,
  cohort: LayersTwo01,
  creator: Users01,
};

type GmvBreakdownCardProps = {
  range: DistributionRange;
  rangeReady: boolean;
  onAsk: (request: AskRequest) => void;
};

export function GmvBreakdownCard({ range, rangeReady, onAsk }: GmvBreakdownCardProps) {
  const [lens, setLens] = useState<GmvBreakdownLens>("product");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const formatGmv = useGmvFormatter();
  const scopedUrl = useScopedUrl();
  const navigate = useNavigate();

  const { data, isFetching } = useHomeGmvBreakdown({ from: range.from, to: range.to }, lens, {
    enabled: rangeReady,
  });
  const shownLens = data?.lens ?? lens;
  const { data: cohorts } = useCreatorCohorts();
  const hasNoCohorts = cohorts !== undefined && cohorts.length === 0;
  const phrase = noteRangePhrase(range);

  const entities = useMemo((): PanelEntity[] => {
    if (!data) return [];

    const named = data.top
      .map((entity) => ({ ...entity, isRemainder: false }))
      .sort((a, b) => b.gmv - b.priorGmv - (a.gmv - a.priorGmv));
    if (data.others.memberCount === 0) return named;

    return [
      ...named,
      {
        id: REMAINDER_ID,
        name: REMAINDER_NAME[shownLens],
        thumbnail: null,
        subtitle: null,
        count: data.others.memberCount,
        countUnit: null,
        color: null,
        gmv: data.others.gmv,
        priorGmv: data.others.priorGmv,
        change: data.others.change,
        lifetimeGmv: null,
        lifetimeVideoCount: null,
        bestVideoGmv: null,
        topCreatorName: null,
        topCreatorSharePct: null,
        periodNewVideoCount: null,
        isRemainder: true,
        members: [...data.others.members].sort((a, b) => b.gmv - b.priorGmv - (a.gmv - a.priorGmv)),
        memberCount: data.others.memberCount,
      },
    ];
  }, [data, shownLens]);

  const columns = useMemo<BreakdownColumn[]>(
    () => entities.map((entity) => ({ id: entity.id, name: entity.name, delta: entity.gmv - entity.priorGmv })),
    [entities],
  );

  // Only product and creator mount a drawer below. Dropping this branch leaves the
  // cohort action setting an id nothing renders, so the button goes dead.
  const openEntity = (entity: PanelEntity) => {
    if (shownLens === "cohort") {
      openLensList();
      return;
    }

    setOpenDetailId(entity.id);
  };

  const openLensList = () => {
    const { path, search } = LENS_DESTINATION[shownLens];
    navigate({ to: `${scopedUrl}${path}`, search });
  };

  // Deep-links straight into the "Manage Cohorts" modal, skipping the extra click of
  // opening it from the Cohorts page.
  const openCreateCohort = () => {
    navigate({ to: `${scopedUrl}${LENS_DESTINATION.cohort.path}`, search: { manageCohorts: true } });
  };

  const switchLens = (next: GmvBreakdownLens) => {
    setLens(next);
    setHoveredId(null);
    setSelectedId(null);
    setOpenDetailId(null);
  };

  const body = !data ? "loading" : columns.length === 0 ? "empty" : "chart";

  const netChange = data ? data.totals.lensTotal.current - data.totals.lensTotal.prior : 0;
  const selected = entities.find((entity) => entity.id === selectedId) ?? null;

  return (
    <section className="flex flex-col gap-4 rounded-[14px] bg-white px-7 py-5 shadow-[0_2px_12px_rgba(66,48,125,0.07)]">
      <header className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          {data ? (
            <>
              <h3 className="text-[22px] leading-8 font-semibold text-[#21133a]">
                {headline(netChange, phrase, formatGmv)}
              </h3>
              <p className="text-[13px] leading-5 text-[#21133a]/70">
                {subline(data.totals.lensTotal.current, data.totals.lensTotal.prior, phrase, formatGmv)}
              </p>
            </>
          ) : (
            <div className="h-[52px] w-[420px] max-w-full animate-pulse rounded-lg bg-gray-200" />
          )}
        </div>

        <div
          role="tablist"
          aria-label="Decompose the change by"
          className="ml-auto inline-flex w-fit shrink-0 items-center gap-1 rounded-full bg-gray-100 p-1"
        >
          {LENS_TABS.map((tab) => {
            const Icon = LENS_ICONS[tab.id];
            const selectedTab = lens === tab.id;

            return (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={selectedTab}
                onClick={() => switchLens(tab.id)}
                style={{ color: selectedTab ? "#21133a" : "#21133a8c" }}
                className={[
                  "inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors duration-150",
                  selectedTab ? "bg-white font-semibold shadow-xs" : "font-medium hover:bg-white/60",
                ].join(" ")}
              >
                <Icon width={15} height={15} aria-hidden="true" className="shrink-0" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {body === "loading" && (
        <div
          data-testid="breakdown-loading"
          style={{ height: BREAKDOWN_COLUMN_HEIGHT + 24 }}
          className="w-full animate-pulse rounded-lg bg-gray-200"
        />
      )}

      {body === "empty" && shownLens === "cohort" && hasNoCohorts && (
        <div className="flex items-center justify-around rounded-xl bg-unbox-cream-50 px-8 py-7 h-[268px]">
          <div className="flex flex-col items-start gap-4">
            <div>
              <p className="text-[16px] font-medium text-[#21133a]">You haven't created any cohorts yet.</p>
              <p className="text-[14px] text-[#21133a]/70">
                Set up cohorts to analyze data against particular creators.
              </p>
            </div>
            <Button color="secondary" size="sm" iconLeading={Plus} onClick={openCreateCohort}>
              Create New Cohort
            </Button>
          </div>
          <img src="/empty-states/cohorts-group.png" alt="" aria-hidden width={336} />
        </div>
      )}

      {body === "empty" && !(shownLens === "cohort" && hasNoCohorts) && (
        <p className="py-14 text-center text-[14px] text-[#21133a]/50">Nothing moved enough to show for this period.</p>
      )}

      {body === "chart" && (
        <div
          style={{ opacity: isFetching ? 0.55 : 1 }}
          className="flex flex-col items-stretch gap-8 transition-opacity duration-150 lg:flex-row"
        >
          <div className="min-w-0 flex-1">
            <GmvBreakdownChart
              columns={columns}
              netChange={netChange}
              hoveredId={hoveredId}
              selectedId={selectedId}
              onHover={setHoveredId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              formatDelta={formatGmv}
              animationKey={`${shownLens}-${range.from}-${range.to}`}
            />
          </div>

          {selected && (
            <GmvBreakdownPanel
              key={selected.id}
              entity={selected}
              lens={shownLens}
              phrase={phrase}
              lensTotal={data?.totals.lensTotal.current ?? 0}
              formatGmv={formatGmv}
              onAsk={onAsk}
              onOpen={openEntity}
              onOpenAll={openLensList}
            />
          )}
        </div>
      )}

      {shownLens === "product" && (
        <ProductDetails id={openDetailId ?? undefined} onClose={() => setOpenDetailId(null)} />
      )}
      {shownLens === "creator" && (
        <CreatorDetails id={openDetailId ?? undefined} onClose={() => setOpenDetailId(null)} />
      )}
    </section>
  );
}
